import { err, ok } from '@src/services/common/Result';

import { MIN_PIECE_SEC } from './clipSpanOverlap';
import {
  leadInDubDelaySec,
  leadInOriginalLevel,
  planBedUnderOriginal,
  planFadesIntoMute,
  planLeadInOriginalRanges,
  planLeadInVoice,
} from './leadIn';
import { planOriginalAudioPieces } from './segmentLevels';

import type {
  ClipSpan,
  DubAudioElementSpec,
  DubPlacementPlan,
} from './DubPlacementService';
import type { BedPiece, DubLeadIn } from './leadIn';
import type { DubSegmentLevels, OriginalLevelRange } from './segmentLevels';
import type { Result } from '@src/services/common/Result';

/**
 * The dub engine: the one place that turns "this clip, this language's dubbed
 * lines, this voice position, this music position" into the changes that put a
 * dub onto a document.
 *
 * It is the merge of the two planners this replaces — the interactive one that
 * served the Voice control and the publish one that served the render — into a
 * single function serving both (PLAT-11511, decision 0005). Pure: no MobX, no
 * React, no network, no editor, no clock.
 *
 * Deliberately parallel to the layout engine. The layout engine applies scene
 * changes when the timeline is turned into a document rather than writing them
 * into the video elements, and this does the same for dub audio. Nothing about
 * the dub is stored on the timeline any more except three options, so this
 * function's answer is recomputed every time a document is produced and can
 * never drift out of step with a picture that has been trimmed or cut.
 *
 * It builds the dub's tracks from nothing on every pass. The only inputs that
 * decide what it produces are the three stored options — the language, the
 * voice position and the music position — plus the placement built from the
 * description held in memory. It never looks at what a document already
 * carries, because the document it is handed is built afresh from a stored
 * timeline that holds no dub audio at all.
 *
 * One idea from the publish planner is kept: **there is a third answer for the
 * main video's level, which is to write nothing.** When this pass places no dub
 * the level on the picture is the one the person set for reasons that have
 * nothing to do with dubbing, so it is left exactly as it stands.
 *
 * One idea from the interactive planner is deliberately NOT kept: it used to
 * leave every other language's dub on the clip, silenced, so a person could
 * switch back without generating it again, and it placed the music bed muted on
 * the positions where it must not be heard. Both existed to keep a stored
 * choice alive in the elements themselves. The choice is now stored directly,
 * in the three dub options, so audio nobody will hear is not put in the
 * document at all — which is also what the render service needs, since it
 * downloads and decodes every file it is handed and never reads a level.
 */

const logPrefix = 'DubEngine';

/**
 * Which voice the person asked for. Restated rather than imported from the
 * on-screen control's `AudioMode`, because the editor must not import from a
 * component; the two stay assignable to each other.
 */
export type DubVoiceMode = 'original' | 'dubbed' | 'both';

/**
 * Whether the recording's own music plays under the dub. Two positions, not
 * three: the bed either replaces the sound the silenced picture used to carry
 * or it does not.
 */
export type DubMusicMode = 'none' | 'original';

/**
 * Which part of the dub an element belongs to. `original` is the recording's
 * own sound, laid back as pieces when a line sets its own original level on
 * Both (`planOriginalAudioPieces`). `leadIn` is the speaker's separated voice
 * — or, before the story is separated, the recording's own sound — moved ahead
 * of the dub (`planLeadInVoice`).
 */
export type DubTrackKind = 'voice' | 'music' | 'original' | 'leadIn';

/**
 * A volume as a percentage string — never a number. The two element classes
 * disagree about what a number means (a video element multiplies it by a
 * hundred, an audio element does not); a string takes both down the same branch.
 */
export type ElementVolume = `${number}%`;

/**
 * The dubbed voice's stored level (PLAT-11899), as a whole percentage below
 * full, or `undefined` for full.
 *
 * Full has no stored form on purpose: a clip nobody turned down must keep the
 * exact stored shape it had before the level existed, or the publish staleness
 * hash would move for every dubbed clip in the library. Anything that is not a
 * finite number reads as full, and the rest is held to 0–100 — never above,
 * because the render has not been shown to honour a boost.
 */
export function normalizeDubVoiceVolume(raw: unknown): number | undefined {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return undefined;
  const level = Math.round(Math.min(100, Math.max(0, raw)));
  return level === 100 ? undefined : level;
}

/** One audio element to create, and the half of the dub it belongs to. */
export interface PlannedDubElement extends DubAudioElementSpec {
  kind: DubTrackKind;
  volume: ElementVolume;
  /**
   * Seconds; under a lead-in only, where the original fades into a mute and
   * the bed fades in against it.
   */
  audio_fade_in?: number;
  audio_fade_out?: number;
}

export interface PlannedVolumeChange {
  elementId: string;
  volume: ElementVolume;
}

export interface DubCompositionPlan {
  /**
   * The voice half first, then the music half (on Dubbed) or the original's
   * pieces (on Both, `segmentLevels`), each in timeline order.
   */
  elementsToCreate: PlannedDubElement[];
  /**
   * The level for the clip's original video, on the passes that decide one.
   * Empty when this pass places no dub, which leaves the picture at whatever
   * level the person set.
   */
  volumeChanges: PlannedVolumeChange[];
  /** Manifest lines the placement dropped, carried through for reporting. */
  skipped: { segmentId: string; reason: string }[];
}

export interface DubCompositionInput {
  /**
   * Where this language's dubbed lines land on this clip, or `null` when the
   * language has no dub, when the description has not arrived yet, or when the
   * placement could not be run at all. `null` is a valid input that places
   * nothing.
   */
  placement: DubPlacementPlan | null;
  /**
   * The clip's kept spans, in timeline order. Unused by the voice half, whose
   * placement was worked out upstream; the music bed is one link for the whole
   * story with no manifest, so the bed half places one element per span.
   */
  spans?: ClipSpan[];
  /** The identifiers of the clip's original-video elements. */
  originalVideoElementIds: readonly string[];
  voiceMode: DubVoiceMode;
  /**
   * The kept music bed's signed link, or nothing. One story-length file on the
   * source recording's own clock: second *n* of the bed is second *n* of the
   * story, with no manifest and no per-segment structure.
   */
  musicBedUrl?: string | null;
  /**
   * `none` when it is not passed, so a caller that knows nothing about music
   * gets the same plan it would get with the music position turned off.
   */
  musicMode?: DubMusicMode;
  /**
   * The dubbed voice's level as stored (PLAT-11899); nothing means full. Sets
   * the voice half only, and the bed plays at full under whatever the voice
   * does.
   */
  voiceVolume?: number;
  /**
   * PLAT-11927 — the picture's level on Both, as stored; nothing means full.
   * Ignored on Dubbed, which silences the picture, and on any pass that places
   * no dub, which leaves the picture at the person's own level.
   */
  originalVolume?: number;
  /**
   * Lines played at their own levels, by `segmentId`. A voice element carrying
   * exactly one such line plays at that line's voice level; the placement keeps
   * those lines in elements of their own (`isolatedSegmentIds`).
   */
  segmentLevels?: DubSegmentLevels;
  /**
   * The original lead-in, on Both. The voice elements already carry any delay
   * (applied to the description, `resolveHeardDub`); this says how long the
   * speaker is heard first and where the original is turned down after. The
   * placement keeps every line in an element of its own while it is set, so a
   * line with its own level can keep it.
   */
  leadIn?: DubLeadIn;
  /**
   * The story's separated voice, which the lead-in moves ahead of the dub.
   * Without it the lead-in moves the recording's own sound instead (from
   * `originalAudioSource`), music and all, with the original silent under it.
   */
  leadInVoiceUrl?: string | null;
  /**
   * The address the clip's picture plays the recording from, for laying its
   * sound back as pieces when a line sets its own original level on Both.
   * Without it a line's original level cannot be honoured, and the picture
   * stays at the clip's level.
   */
  originalAudioSource?: string | null;
}

const FULL_VOLUME: ElementVolume = '100%';
const SILENT: ElementVolume = '0%';

/** Unique, non-blank identifiers in input order. */
function distinctIds(ids: readonly string[] | undefined): string[] {
  if (!Array.isArray(ids)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of ids) {
    if (typeof raw !== 'string' && typeof raw !== 'number') continue;
    const id = String(raw).trim();
    if (id === '' || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/**
 * Describe the changes that put one language's dub onto one document.
 *
 * Returns a validation error only when the input cannot be used at all. A
 * language with no dub, and a placement whose every line falls outside the
 * clip's kept spans, are both valid plans that create nothing.
 */
export function planDubComposition(
  input: DubCompositionInput,
): Result<DubCompositionPlan> {
  const {
    placement,
    spans,
    originalVideoElementIds,
    voiceMode,
    musicBedUrl,
    musicMode,
    voiceVolume,
    originalVolume: originalVolumeOnBoth,
    segmentLevels,
    leadIn,
    leadInVoiceUrl,
    originalAudioSource,
  } = input;

  if (placement && !Array.isArray(placement.elements)) {
    return err(
      'VALIDATION',
      `${logPrefix}: the dub placement plan has no elements array`,
    );
  }
  if (
    voiceMode !== 'original' &&
    voiceMode !== 'dubbed' &&
    voiceMode !== 'both'
  ) {
    return err(
      'VALIDATION',
      `${logPrefix}: unknown voice mode ${String(voiceMode)}`,
    );
  }

  // Nothing is placed on the Original position. Under the stored-track model
  // the dub used to be placed and turned down to silence so a person could
  // switch back without generating it again; the switch back now costs nothing
  // because the description it is built from is held in memory, so audio nobody
  // will hear is left out of the document entirely.
  //
  // A voice turned all the way down is still placed: it is the person's own
  // choice, and leaving it out would hand the picture back its speech.
  const voiceLevel = normalizeDubVoiceVolume(voiceVolume);
  const voiceElementVolume: ElementVolume =
    voiceLevel === undefined ? FULL_VOLUME : `${voiceLevel}%`;
  // A line with its own level is placed alone, so an element carrying exactly
  // one line is the only one that can take that line's level. One line can
  // still be named twice: its pieces either side of a seam rejoin.
  const ownLevel = (segmentIds: readonly string[]) =>
    segmentIds.length > 0 && segmentIds.every(id => id === segmentIds[0])
      ? segmentLevels?.[segmentIds[0]]
      : undefined;
  const elementsToCreate: PlannedDubElement[] =
    voiceMode === 'original'
      ? []
      : (placement?.elements ?? []).map(element => {
          const lineVoice = ownLevel(element.segmentIds)?.voiceVolume;
          return {
            ...element,
            kind: 'voice',
            volume:
              lineVoice === undefined ? voiceElementVolume : `${lineVoice}%`,
          };
        });

  // Captured while the list holds only the voice half; the bed's own elements
  // are appended below, so reading the length any later answers a different
  // question.
  const placesVoice = elementsToCreate.length > 0;

  // Down to silence only when a dub element was actually created: with nothing
  // to hear in its place, silencing the original speech would deliver a silent
  // clip and report success.
  //
  // `null` is the other answer — write no level for the picture at all — and it
  // is what every pass that places no dub gets, so the level the person set for
  // their own reasons is left exactly as it stands. Nothing has to be put back
  // up to full: the level this pass writes goes into the document being built,
  // never onto the stored timeline, so the next pass starts from the person's
  // own level again.
  //
  // On Both the picture plays at its stored level (PLAT-11927), full when none
  // is stored. Like the voice, a level of 0 is still the person's own choice.
  const originalLevel = normalizeDubVoiceVolume(originalVolumeOnBoth);

  // A line's own original level, where it is heard: on Both, over the stretch
  // its dubbed voice plays. The picture is one element and cannot change level
  // part-way, so when any line has one the picture is silenced and its sound
  // laid back as pieces, each at its own level.
  //
  // A lead-in is the same idea once, at the clip's first line: the original at
  // its lead-in level until the lead-in ends, then at the clip's level under
  // the dub for the rest of the clip (a line's own level under its own dub).
  const leadInOnBoth = voiceMode === 'both' ? leadIn : undefined;
  // The speaker's own voice ahead of the dub, from where the first line was
  // spoken (the earliest voice element, less the dub's delay). Before the
  // story is separated it is the recording's own sound, room and music too.
  const leadInSource = leadInVoiceUrl || originalAudioSource || null;
  const firstLineSec =
    elementsToCreate.length > 0 && leadInOnBoth
      ? Math.min(...elementsToCreate.map(element => element.time)) -
        leadInDubDelaySec(leadInOnBoth)
      : null;
  const leadInVoice =
    leadInOnBoth && leadInSource && firstLineSec !== null
      ? planLeadInVoice(
          firstLineSec,
          Array.isArray(spans) ? spans : [],
          leadInOnBoth,
        )
      : null;
  const originalRanges: OriginalLevelRange[] = [];
  if (leadInOnBoth && originalAudioSource) {
    // The recording's own sound moved ahead carries the room and the music,
    // so the original is silent under it too: one copy at a time.
    if (
      !leadInVoiceUrl &&
      leadInVoice &&
      firstLineSec !== null &&
      leadInVoice.time < firstLineSec
    ) {
      originalRanges.push({
        startSec: leadInVoice.time,
        endSec: firstLineSec,
        level: 0,
      });
    }
    originalRanges.push(
      ...planLeadInOriginalRanges(
        elementsToCreate,
        leadInOnBoth,
        originalLevel ?? 100,
        segmentIds => ownLevel(segmentIds)?.originalVolume,
        Math.max(
          0,
          ...(spans ?? []).map(span => span.clipStartSec + span.durationSec),
        ),
        leadInVoice?.duration ?? 0,
      ),
    );
  } else if (voiceMode === 'both' && originalAudioSource) {
    for (const element of elementsToCreate) {
      const lineOriginal = ownLevel(element.segmentIds)?.originalVolume;
      if (lineOriginal === undefined) continue;
      originalRanges.push({
        startSec: element.time,
        endSec: element.time + element.duration,
        level: lineOriginal,
      });
    }
  }
  const layOriginalPieces =
    originalRanges.length > 0 && Array.isArray(spans) && spans.length > 0;

  // A clip that ends before its lead-in does leaves no range at all: the
  // original is at its lead-in level the whole way, not at the clip's Both
  // level.
  const leadInKeepsLevel =
    !!leadInOnBoth && !!originalAudioSource && !layOriginalPieces;

  const originalVolume: ElementVolume | null = layOriginalPieces
    ? SILENT
    : placesVoice
      ? voiceMode === 'both'
        ? leadInKeepsLevel
          ? `${leadInOriginalLevel(leadInOnBoth)}%`
          : originalLevel === undefined
            ? FULL_VOLUME
            : `${originalLevel}%`
        : SILENT
      : null;

  // Four separate conditions rather than one, because each is a different
  // reason for a clip to end up with no music: the person asked for none;
  // separation has produced no bed for this story; the clip has no kept stretch
  // to place one over; or this pass places no voice at all, so a bed would be
  // music under a dub that is not there.
  //
  // On Dubbed the picture is silenced and the bed plays at full. On Both the
  // picture keeps its own sound, music and all, so the bed makes up only what
  // the original is turned down by: none at full, all of it at `0%`, so the
  // music stays whole and is never heard twice. On Original nothing is placed.
  // The bed and the separated voice are the same recording split in two, so
  // the original at L% and the bed at (100 − L)% put the music back whole.
  const keptSpans = Array.isArray(spans) ? spans : [];
  const bedUrl = typeof musicBedUrl === 'string' ? musicBedUrl : '';
  const placeBed =
    musicMode === 'original' &&
    placesVoice &&
    (voiceMode === 'dubbed' || voiceMode === 'both') &&
    bedUrl !== '' &&
    keptSpans.length > 0;

  // Where the dubbed voice's coverage ends on the clip's own timeline: the far
  // edge of the last voice element.
  //
  // The bed must stop HERE, not at the clip's end (PLAT-10628): a story dubbed
  // partway, then clipped, leaves the clip's tail with no voice, and a bed
  // running under that tail is music under nothing.
  const coverageEndSec = placesVoice
    ? Math.max(...elementsToCreate.map(e => e.time + e.duration))
    : 0;

  const pieces =
    layOriginalPieces && originalAudioSource
      ? planOriginalAudioPieces(
          keptSpans,
          originalRanges,
          // Outside the ranges: the lead-in's own level under a lead-in, which
          // is the clip's opening up to the end of the lead-in; the clip's Both
          // level otherwise.
          leadInOnBoth
            ? leadInOriginalLevel(leadInOnBoth)
            : (originalLevel ?? 100),
        ).filter(piece => piece.duration >= MIN_PIECE_SEC)
      : [];
  const fades = leadInOnBoth?.fadeSec
    ? planFadesIntoMute(pieces, leadInOnBoth.fadeSec)
    : [];

  // The bed and the picture share the source recording's clock, so a span's
  // three numbers are the element's three numbers unchanged. One element laid
  // across the whole clip instead would play the bed straight through the
  // source and slide the music against the picture by the length of everything
  // the clip cut out.
  // The picture's own level on Both, where it is one element all the way.
  const pictureLevelOnBoth =
    leadInKeepsLevel && leadInOnBoth
      ? leadInOriginalLevel(leadInOnBoth)
      : (originalLevel ?? 100);
  const bedPieces: BedPiece[] = !placeBed
    ? []
    : voiceMode === 'dubbed'
      ? keptSpans.map(span => ({
          time: span.clipStartSec,
          duration: span.durationSec,
          trim_start: span.sourceStartSec,
          level: 100,
        }))
      : pieces.length > 0
        ? planBedUnderOriginal(pieces, fades)
        : keptSpans.map(span => ({
            time: span.clipStartSec,
            duration: span.durationSec,
            trim_start: span.sourceStartSec,
            level: 100 - pictureLevelOnBoth,
          }));

  for (const bedPiece of bedPieces) {
    // A stretch lying entirely past the coverage edge gets no bed element, and
    // the floor is the placement's own sliver floor, so the two halves of the
    // dub agree on what is too short to be worth an element.
    const bedDurationSec = Math.min(
      bedPiece.duration,
      coverageEndSec - bedPiece.time,
    );
    if (bedDurationSec < MIN_PIECE_SEC || bedPiece.level <= 0) continue;
    elementsToCreate.push({
      type: 'audio',
      source: bedUrl,
      time: bedPiece.time,
      duration: bedDurationSec,
      trim_start: bedPiece.trim_start,
      // The bed is one story-length file, not a generated one, so it carries
      // no manifest line and no rendition.
      segmentIds: [],
      renditionId: '',
      kind: 'music',
      // A string, never the number 100: an audio element reads a number as a
      // fraction and multiplies it by a hundred, storing `100` as `10000%`.
      volume: bedPiece.level === 100 ? FULL_VOLUME : `${bedPiece.level}%`,
      // No `volume_keypoints`, ever: the render route explodes an audio
      // element carrying them across two tracks, one invented on the spot.
      ...(bedPiece.fadeInSec !== undefined && {
        audio_fade_in: bedPiece.fadeInSec,
      }),
      // A fade cut off at the coverage edge would end above silence.
      ...(bedPiece.fadeOutSec !== undefined &&
        bedDurationSec === bedPiece.duration && {
          audio_fade_out: bedPiece.fadeOutSec,
        }),
    });
  }

  if (pieces.length > 0 && originalAudioSource) {
    pieces.forEach((piece, i) => {
      const fade = fades[i];
      elementsToCreate.push({
        type: 'audio',
        source: originalAudioSource,
        time: piece.time,
        duration: piece.duration,
        trim_start: piece.trim_start,
        segmentIds: [],
        renditionId: '',
        kind: 'original',
        volume: `${piece.level}%`,
        // A fade, never `volume_keypoints`, for the reason the bed carries none.
        ...(fade?.fadeInSec !== undefined && {
          audio_fade_in: fade.fadeInSec,
        }),
        ...(fade?.fadeOutSec !== undefined && {
          audio_fade_out: fade.fadeOutSec,
        }),
      });
    });
  }

  if (leadInOnBoth && leadInVoice && leadInSource) {
    const level = leadInOriginalLevel(leadInOnBoth);
    elementsToCreate.push({
      type: 'audio',
      source: leadInSource,
      time: leadInVoice.time,
      duration: leadInVoice.duration,
      trim_start: leadInVoice.trim_start,
      segmentIds: [],
      renditionId: '',
      kind: 'leadIn',
      volume: level === 100 ? FULL_VOLUME : `${level}%`,
      ...(leadInVoice.fadeOutSec !== undefined && {
        audio_fade_out: leadInVoice.fadeOutSec,
      }),
    });
  }

  // Each picture element is named once, so the level it ends at is decided
  // once: a repeat would make an applier capture the level it had just written
  // and then put that back on the way out.
  const volumeChanges: PlannedVolumeChange[] =
    originalVolume === null
      ? []
      : distinctIds(originalVideoElementIds).map(elementId => ({
          elementId,
          volume: originalVolume,
        }));

  return ok({
    elementsToCreate,
    volumeChanges,
    skipped: [...(placement?.skipped ?? [])],
  });
}

/** Does this plan change anything at all? */
export function dubPlanIsEmpty(plan: DubCompositionPlan): boolean {
  return plan.elementsToCreate.length === 0 && plan.volumeChanges.length === 0;
}
